import React, { lazy, useContext, useEffect, useState, Suspense } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import contents from '../../routes/contentRoutes';
import { UserType } from '../../common/data/dummyRolesData';
import RoleProtectedRoute from '../../components/auth/RoleProtectedRoute';
import AuthContext from '../../contexts/authContext';
import Loading from '../../components/Loading';

const PAGE_404 = lazy(() => import('../../pages/presentation/auth/Page404'));
const UNAUTHORIZED = lazy(() => import('../../pages/presentation/auth/Unauthorized'));
// const LOGIN = lazy(() => import('../../pages/presentation/auth/Login'));

const ContentRoutes = () => {
	const { isAuthenticated, isLoading, isBootstrapped, userData } = useContext(AuthContext);
	const location = useLocation();
	const userRole = userData?.role || null;
	const [showUnauthorized, setShowUnauthorized] = useState(false);

	// Role-based URL protection
	useEffect(() => {
		if (isLoading || !isAuthenticated || !userRole || !isBootstrapped) return;

		const path = location.pathname;

		// Allow /talents/:id for both user types (public talent profiles)
		if (path.startsWith('/talents/')) {
			setShowUnauthorized(false);
			return;
		}

		if (userRole === UserType.COMPANY && path.startsWith('/talent')) {
			setShowUnauthorized(true);
			return;
		}

		if (userRole === UserType.FREELANCE && path.startsWith('/company')) {
			setShowUnauthorized(true);
			return;
		}

		setShowUnauthorized(false);
	}, [isLoading, isAuthenticated, isBootstrapped, userRole, location.pathname]);

	// Blocca il routing finché il bootstrap non è completo
	if (isLoading || !isBootstrapped) {
		return <Loading />;
	}

	// Mostra pagina "Unauthorized" se il controllo del ruolo fallisce
	if (showUnauthorized) {
		return <UNAUTHORIZED />;
	}

	return (
		<Suspense fallback={<Loading />}>
			<Routes>
				{/* Redirect root path in base al ruolo */}
				<Route
					path='/'
					element={
						isAuthenticated ? (
							userRole === UserType.COMPANY ? (
								<Navigate to='/company/dashboard' replace />
							) : userRole === UserType.FREELANCE ? (
								<Navigate to='/talent/dashboard' replace />
							) : (
								<UNAUTHORIZED />
							)
						) : (
							<UNAUTHORIZED />
						)
					}
				/>

				{/* Mappatura delle route dai contenuti */}
				{contents.map((page) => {
					if (!page.path) return null;

					if (page.path.startsWith('/company')) {
						return (
							<Route
								key={page.path}
								path={page.path}
								element={
									<RoleProtectedRoute
										allowedRoles={[UserType.COMPANY]}
										unauthorizedComponent={<UNAUTHORIZED />}>
										{page.element}
									</RoleProtectedRoute>
								}
							/>
						);
					}

					if (page.path.startsWith('/talent')) {
						return (
							<Route
								key={page.path}
								path={page.path}
								element={
									<RoleProtectedRoute
										allowedRoles={[UserType.FREELANCE]}
										unauthorizedComponent={<UNAUTHORIZED />}>
										{page.element}
									</RoleProtectedRoute>
								}
							/>
						);
					}

					// Altre route (pubbliche o solo con autenticazione)
					return <Route key={page.path} {...page} />;
				})}

				{/* 404 */}
				<Route path='/auth-pages/404' element={<PAGE_404 />} />
				<Route path='*' element={<Navigate to='/auth-pages/404' replace />} />
			</Routes>
		</Suspense>
	);
};

export default ContentRoutes;
